import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button, Dialog, Field, Input } from '@zt/ui';
import { api, ApiError, registerStepUpHandler } from '../lib/api';
import { useT } from '../lib/i18n';

/** Hassas işlemlerde API "step_up_required" dönerse kodu yeniden ister ve isteği tekrarlar. */
export function StepUpDialog() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  useEffect(() => {
    registerStepUpHandler(
      () =>
        new Promise<boolean>((resolve) => {
          resolver.current = resolve;
          setCode('');
          setError(undefined);
          setOpen(true);
        }),
    );
    return () => registerStepUpHandler(null);
  }, []);

  const close = (ok: boolean) => {
    setOpen(false);
    resolver.current?.(ok);
    resolver.current = null;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/v1/auth/mfa/verify', useBackup ? { backupCode: code } : { code: code.trim() }, {
        noStepUp: true,
      });
      close(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.title : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && close(false)}
      title={t('admin.auth.stepUpTitle')}
      description={t('admin.auth.stepUpLead')}
      closeLabel={t('common.close')}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label={useBackup ? t('admin.auth.backupCode') : t('admin.auth.code')} error={error} required>
          {(p) => (
            <Input
              {...p}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode={useBackup ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              className="tabular text-lg tracking-[0.3em]"
              maxLength={useBackup ? 12 : 6}
            />
          )}
        </Field>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className="text-sm text-pine underline-offset-2 hover:underline"
            onClick={() => setUseBackup((v) => !v)}
          >
            {useBackup ? t('admin.auth.useCode') : t('admin.auth.useBackup')}
          </button>
          <Button type="submit" variant="primary" loading={busy}>
            {t('admin.auth.stepUpSubmit')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
